import React, { useState, useEffect, useCallback, useRef } from "react";
import useQRScanner from "../utils/useQrCode";
import { Alert, Button, Card, Form } from "react-bootstrap";
import { VideoScanBar, VideoWrapper } from "./Login.style";
import UserCard, { User } from "./UserCard";
import DeviceCard, { Device } from "./DeviceCard";
import { fetchAPI } from "../utils/fetchAPI";
import { useNavigate } from "react-router-dom";

const QRScannerWithSteps = () => {
  const navigate = useNavigate();
  const { videoRef, qrCode, error: cameraError, setQrCode } = useQRScanner();
  const [userData, setUserData] = useState<User>();
  const [deviceData, setDeviceData] = useState<Device>();
  const [errorMessage, setErrorMessage] = useState("");
  const [infoMessage, setInfoMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [relationExists, setRelationExists] = useState(false);
  const [isAssignedToOtherUser, setIsAssignedToOtherUser] = useState(false);
  const [stockAmount, setStockAmount] = useState<number | null>(null);

  // Prosty debouncing
  const lastProcessedQr = useRef<string>("");
  const processingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Debounced function do przetwarzania QR kodu
  const processQRCode = useCallback(
    async (qr: string) => {
      if (qr === lastProcessedQr.current) return;

      lastProcessedQr.current = qr;
      setIsLoading(true);
      setErrorMessage("");
      setInfoMessage("");

      try {
        const data = await fetchAPI("POST", "checkqr.php", { qr });

        if (data?.success) {
          if (data.type === 1) {
            setUserData(data.data);
          } else if (data.type === 2) {
            setDeviceData(data.data);
          } else if (data.type === 3) {
            // Redirect to steel warehouse with parameters
            const { grade, thickness } = data.data;
            navigate(`/magazyn-stali?grade=${encodeURIComponent(grade)}&thickness=${encodeURIComponent(thickness)}`);
          }
        } else {
          setErrorMessage(data?.message || "Nieudane przetwarzanie kodu. Spróbuj ponownie.");
        }
      } catch (error) {
        setErrorMessage("Błąd podczas przetwarzania kodu QR.");
      } finally {
        setIsLoading(false);
      }
    },
    [navigate]
  );

  // Debounced effect dla QR kodu
  useEffect(() => {
    if (!qrCode) return;

    // Wyczyść poprzedni timeout
    if (processingTimeoutRef.current) {
      clearTimeout(processingTimeoutRef.current);
    }

    // Debounce 300ms
    processingTimeoutRef.current = setTimeout(() => {
      processQRCode(qrCode);
    }, 300);

    return () => {
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
      }
    };
  }, [qrCode, processQRCode]);

  useEffect(() => {
    const checkRelation = async () => {
      if (userData?.id && deviceData?.id) {
        const result = await fetchAPI("POST", "setdevice.php", {
          userId: userData.id,
          deviceId: deviceData.id,
          action: "check",
        });

        if (result?.success) {
          setRelationExists(result.exists);
          setIsAssignedToOtherUser(result.isAssigned && !result.exists);

          if (result.isAssigned && !result.exists) {
            setErrorMessage(result.message || "Urządzenie jest już przypisane do innego użytkownika.");
          }
        } else {
          setErrorMessage(result?.message || "Błąd podczas sprawdzania relacji.");
        }
      }
    };

    checkRelation();
  }, [userData, deviceData]);

  const handleDeviceAction = async () => {
    if (!userData?.id || !deviceData?.id || (isAssignedToOtherUser && !relationExists)) return;
    setIsLoading(true);
    setErrorMessage("");
    setInfoMessage("");

    const action = relationExists ? "unassign" : "assign";
    const payload: any = { userId: userData.id, deviceId: deviceData.id, action };

    if (!relationExists && deviceData.returnable === 0) {
      payload.count = stockAmount;
    }

    const result = await fetchAPI("POST", "setdevice.php", payload);

    if (result?.success) {
      setInfoMessage(result.message);
      resetState();
    } else {
      setErrorMessage(result?.message || "Nie udało się wykonać operacji.");
    }

    setIsLoading(false);
  };

  const resetState = () => {
    setDeviceData(undefined);
    setRelationExists(false);
    setIsAssignedToOtherUser(false);
    setStockAmount(null);
    lastProcessedQr.current = "";
  };

  const resetStateAll = () => {
    setUserData(undefined);
    setDeviceData(undefined);
    setRelationExists(false);
    setIsAssignedToOtherUser(false);
    setStockAmount(null);
    lastProcessedQr.current = "";
    setQrCode(null);
  };

  // Cleanup przy unmount
  useEffect(() => {
    return () => {
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="container">
      <VideoWrapper className="mb-3 mt-3">
        <video ref={videoRef} style={{ width: "100%" }} />
        <VideoScanBar />
      </VideoWrapper>

      {cameraError && (
        <Alert variant="warning" className="mb-3">
          {cameraError}
        </Alert>
      )}

      {(userData || deviceData) && <div className="mb-2">Wynik skanowania:</div>}
      <Card className="p-2 pb-0 bg-brand mb-3">
        {!userData && !deviceData && <p className="text-center mt-3">Zeskanuj kod...</p>}
        {deviceData && !userData && <p className="text-center mt-3">Zeskanuj kod Pracownika...</p>}
        {userData && <UserCard data={userData} />}
        {userData && !deviceData && <p className="text-center mt-3">Zeskanuj kod urządzenia...</p>}
        {deviceData && <DeviceCard data={deviceData} />}
      </Card>

      {isLoading && (
        <p className="text-center">
          <div className="loader"></div>Przetwarzanie kodu...
        </p>
      )}
      {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
      {infoMessage && <Alert variant="success">{infoMessage}</Alert>}

      {userData && deviceData && (
        <>
          {deviceData.status !== 1 ? (
            <Alert variant="warning">Urządzenie uszkodzone lub w naprawie</Alert>
          ) : (
            <>
              {!relationExists && deviceData.returnable === 0 && (
                <Form.Group className="mt-3">
                  <Form.Label>Podaj ilość sztuk</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    max={deviceData.count ? deviceData.count : 0}
                    value={stockAmount ?? ""}
                    onChange={(e) =>
                      setStockAmount(
                        Number(deviceData.count) >= Number(e.target.value) ? Number(e.target.value) : deviceData.count
                      )
                    }
                  />
                </Form.Group>
              )}
              <Button
                className="w-100 mt-3 pt-4 pb-4 fade-in box"
                variant={relationExists ? "warning" : "success"}
                onClick={handleDeviceAction}
                disabled={isAssignedToOtherUser && !relationExists}
              >
                {relationExists ? "Zwróć do magazynu" : "Wydaj sprzęt pracownikowi"}
              </Button>
            </>
          )}
        </>
      )}

      {(userData || deviceData) && (
        <Button className="w-100 mt-4 pt-2 pb-2" variant="outline-primary" onClick={resetStateAll}>
          wyczyść
        </Button>
      )}
    </div>
  );
};

export default QRScannerWithSteps;
